import { version } from 'react';
import { twJoin } from 'tailwind-merge';

import type { FC, ReactNode } from 'react';

interface CollapsiblePanelProps {
  id: string;
  labelledBy: string;
  isOpen: boolean;
  children?: ReactNode;
  className?: string;
}

// React 18 doesn't know `inert` and only renders it as the string attribute (`inert=""`); React 19 treats it
// as a boolean and would read "" as false. Pick the value each version renders as `inert`.
const INERT = (Number(version.split('.')[0]) >= 19 ? true : '') as unknown as boolean;

/**
 * Accordion panel whose content is always in the HTML and in the page's rendered text.
 *
 * Radix's Accordion Content never renders a closed item's children (not even with forceMount), so closed
 * panels were missing from the server HTML and LLM fetchers / text extractors only saw the open one. This
 * panel is always rendered and collapsed with a 0fr grid row + overflow clipping (same duration and easing
 * as the Radix height animation).
 *
 * Closed panels are not hidden with the `hidden` attribute, aria-hidden, display:none or visibility:hidden:
 * extractors drop the first two, and the last two also remove the text from `innerText`, which browser-based
 * agents (Claude in Chrome, Playwright, etc.) read. `inert` is used instead so links inside a closed panel
 * can't be reached by keyboard, without affecting rendering or `innerText`.
 *
 * Pair it with a Radix Trigger that gets `id={labelledBy}` and `aria-controls={id}`.
 */
const CollapsiblePanel: FC<CollapsiblePanelProps> = ({ id, labelledBy, isOpen, children, className }) => (
  <div
    id={id}
    role="region"
    aria-labelledby={labelledBy}
    inert={isOpen ? undefined : INERT}
    className={twJoin(
      'grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.87,0,0.13,1)]',
      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
    )}
  >
    <div className={twJoin('min-h-0 overflow-hidden', className)}>{children}</div>
  </div>
);

export default CollapsiblePanel;
